"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AssistantOnboardingHeader from "../../components/AssistantOnboardingHeader";
import { UploadCloud, X, ChevronDown } from "lucide-react";
import providersService from "../../api/services/providersService";
import {
  assistantServiceOptions as serviceOptions,
  normalizeAssistantServices,
  serializeAssistantServices,
} from "../../data/assistantServiceOptions";
import useEditFlow from "../../hooks/useEditFlow";
import { getStoredUserIdentifier } from "../../utils/authSession";
import {
  NIGERIA_STATES as stateOptions,
  getStateLabel,
  normalizeNigeriaState,
} from "../../constants/nigeriaStates";
import {
  ACCEPTED_IMAGE_ACCEPT,
  MAX_IMAGE_SIZE_MB,
  isAcceptedImage,
  validateImageFile,
} from "../../constants/imageUpload";
import { resizeImageForUpload } from "../../utils/imageResize";
import { useToast } from "../../hooks/useToast";

export default function AssistantKyc({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const stateListRef = useRef(null);
  const fromProfile = Boolean(location.state?.fromProfile);
  const isEditingProfile = useEditFlow(fromProfile);
  const toast = useToast();

  const unwrapResponse = (response) => {
    if (!response) return {};
    return (
      response.data?.data?.profile ||
      response.data?.data?.user ||
      response.data?.data ||
      response.data?.profile ||
      response.data ||
      response.profile ||
      response
    );
  };

  // Use a per-user storage key
  const userIdentifier = currentUser?.id || currentUser?.email || getStoredUserIdentifier() || "guest";
  const storageKey = `assistantKycDraft_${userIdentifier}`;

  const [description, setDescription] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [stateQuery, setStateQuery] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoSelected, setPhotoSelected] = useState(false);
  const [hasPersistedPhoto, setHasPersistedPhoto] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreparingPhoto, setIsPreparingPhoto] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Match on the display label as well as the stored name, so searching
  // "abuja" still surfaces the Federal Capital Territory.
  const filteredStateOptions = stateQuery.trim()
    ? stateOptions.filter((option) =>
        `${option} ${getStateLabel(option)}`
          .toLowerCase()
          .includes(stateQuery.trim().toLowerCase())
      )
    : stateOptions;

  const normalizeStateOption = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "object") {
      const candidate = value.name || value.state || value.label || value.value;
      return normalizeStateOption(candidate);
    }
    // Anything that is not a canonical state resolves to "" — the profile
    // endpoint rejects non-canonical names, so an unrecognised legacy value
    // has to be re-picked rather than silently resubmitted.
    return normalizeNigeriaState(String(value));
  }, []);

  // Toggle services
  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!stateListRef.current) return;
      if (!stateListRef.current.contains(event.target)) {
        setIsStateOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load draft for current user
  useEffect(() => {
    if (isEditingProfile) {
      setIsHydrated(true);
      return;
    }
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setDescription("");
      setSelectedState("");
      setSelectedServices([]);
      setPhotoPreview("");
      setPhotoFile(null);
      setPhotoSelected(false);
      setHasPersistedPhoto(false);
      setHasDraft(false);
      setIsHydrated(true);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setDescription(parsed.description || "");
      setSelectedState(normalizeStateOption(parsed.selectedState || ""));
      setSelectedServices(normalizeAssistantServices(parsed.selectedServices));
      setPhotoPreview(parsed.photoPreview || "");
      setPhotoSelected(Boolean(parsed.photoSelected));
      setHasPersistedPhoto(false);
      setHasDraft(true);
    } catch (err) {
      // Ignore malformed drafts
    } finally {
      setIsHydrated(true);
    }
  }, [isEditingProfile, storageKey, normalizeStateOption]);

  // Load profile if no draft
  useEffect(() => {
    if (hasDraft || isEditingProfile) return;
    let active = true;
    const fetchProfile = async () => {
      try {
        const response = await providersService.getProfile();
        const profile = unwrapResponse(response);
        if (!active || !profile) return;
        setDescription(profile.bio || "");
        const profileState =
          profile.state ||
          profile.stateName ||
          profile.state_name ||
          profile.location?.state ||
          profile.address?.state ||
          profile.cityState;
        setSelectedState(normalizeStateOption(profileState || ""));
        setSelectedServices(normalizeAssistantServices(profile.services));
        const pictureUrl =
          profile.profilePicture?.url ||
          (typeof profile.profilePicture === "string" ? profile.profilePicture : "");
        setPhotoPreview(pictureUrl || "");
        setPhotoSelected(Boolean(pictureUrl));
        setHasPersistedPhoto(Boolean(pictureUrl));
      } catch (err) {
        // Ignore fetch errors
      }
    };
    fetchProfile();
    return () => {
      active = false;
    };
  }, [hasDraft, isEditingProfile, normalizeStateOption]);

  useEffect(() => {
    if (!isEditingProfile) return;
    let active = true;
    const fetchProfile = async () => {
      try {
        const response = await providersService.getProfile();
        const profile = unwrapResponse(response);
        if (!active || !profile) return;
        setDescription(profile.bio || "");
        const profileState =
          profile.state ||
          profile.stateName ||
          profile.state_name ||
          profile.location?.state ||
          profile.address?.state ||
          profile.cityState;
        setSelectedState(normalizeStateOption(profileState || ""));
        setSelectedServices(normalizeAssistantServices(profile.services));
        const pictureUrl =
          profile.profilePicture?.url ||
          (typeof profile.profilePicture === "string" ? profile.profilePicture : "");
        setPhotoPreview(pictureUrl || "");
        setPhotoSelected(Boolean(pictureUrl));
        setHasPersistedPhoto(Boolean(pictureUrl));
        setHasDraft(false);
      } catch (err) {
        // Ignore fetch errors
      } finally {
        if (active) {
          setIsHydrated(true);
        }
      }
    };
    fetchProfile();
    return () => {
      active = false;
    };
  }, [isEditingProfile, normalizeStateOption]);

  // Save draft whenever fields change
  useEffect(() => {
    if (!isHydrated || isEditingProfile) return;
    const draft = {
      description,
      selectedState,
      selectedServices,
      photoPreview,
      photoSelected,
    };
    localStorage.setItem(storageKey, JSON.stringify(draft));
  }, [
    description,
    selectedState,
    selectedServices,
    photoPreview,
    photoSelected,
    isHydrated,
    storageKey,
    isEditingProfile,
  ]);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsPreparingPhoto(true);
    // Shrink camera-sized photos before they go anywhere near the network —
    // this is the difference between a snappy upload and a stalled one on
    // mobile data. Falls back to the original file if the browser can't.
    const uploadFile = await resizeImageForUpload(file);
    setIsPreparingPhoto(false);

    setPhotoFile(uploadFile);
    setPhotoSelected(true);
    setHasPersistedPhoto(false);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result?.toString() || "");
    reader.readAsDataURL(uploadFile);
  };

  const isFormComplete =
    Boolean(description.trim()) &&
    Boolean(selectedState) &&
    selectedServices.length > 0 &&
    (isEditingProfile
      ? photoSelected
      : Boolean(isAcceptedImage(photoFile) || hasPersistedPhoto));

  const handleContinue = async () => {
    if (!description.trim()) {
      toast.error("Please add a short description.");
      return;
    }
    if (!selectedState) {
      toast.error("Please select your state.");
      return;
    }
    if (!selectedServices.length) {
      toast.error("Please select at least one service.");
      return;
    }
    if (!isEditingProfile && !photoFile && !hasPersistedPhoto) {
      toast.error("Please upload a profile photo.");
      return;
    }
    if (photoFile) {
      const photoError = validateImageFile(photoFile);
      if (photoError) {
        toast.error(photoError);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (isEditingProfile) {
        await providersService.updateProfile({
          bio: description.trim(),
          state: selectedState,
          services: serializeAssistantServices(selectedServices),
          profilePicture: photoFile || undefined,
        });
      } else {
        await providersService.upsertProfile({
          bio: description.trim(),
          state: selectedState,
          services: serializeAssistantServices(selectedServices),
          profilePicture: photoFile || undefined,
        });
      }

      // Clear draft after successful submission
      localStorage.removeItem(storageKey);

      if (fromProfile) {
        navigate("/assistant/experience", { state: { fromProfile: true } });
      } else {
        navigate("/assistant/experience");
      }
    } catch (err) {
      toast.apiError(err, "Unable to save your profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <AssistantOnboardingHeader />

      <main className="mx-auto flex max-w-4xl flex-col px-4 pb-16 pt-10">
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Personal Information</h1>
          </div>

          <div className="h-2 w-full overflow-hidden rounded-full bg-[#d3d6dd]">
            <div className="h-full w-[20%] rounded-full bg-[#7a0051]" />
          </div>

          <div className="text-center">
            <p className="text-md font-medium text-gray-600">
              Provide your personal details to create your Pulo assistant profile
            </p>
          </div>
        </div>

                  <div className="space-y-3">
            <label className="text- font-medium text-gray-800">
              State of Residence <span className="font-bold text-red-600">*</span>
            </label>
            <div className="relative" ref={stateListRef}>
              <button
                type="button"
                onClick={() => setIsStateOpen((prev) => !prev)}
                className="flex w-full items-center justify-between rounded-lg border border-[#d8dbe0] bg-white px-4 py-3 text-base font-medium text-gray-800 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
              >
                <span>{getStateLabel(selectedState) || "Select state"}</span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition ${
                    isStateOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isStateOpen && (
                <div className="absolute left-0 right-0 z-10 mt-2 max-h-60 overflow-auto rounded-2xl border border-[#d8dbe0] bg-white shadow-lg">
                  <div className="px-3 pb-2">
                    <input
                      type="text"
                      value={stateQuery}
                      onChange={(e) => setStateQuery(e.target.value)}
                      placeholder="Search state"
                      className="w-full rounded-lg border border-[#d8dbe0] px-3 py-2 text-sm text-gray-700 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
                    />
                  </div>
                  <div className="max-h-48 overflow-auto">
                    {filteredStateOptions.length ? (
                      filteredStateOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            setSelectedState(option);
                            setIsStateOpen(false);
                            setStateQuery("");
                          }}
                          className={`w-full px-4 py-3 text-left text-sm font-medium text-gray-800 hover:bg-[#f4edef] ${
                            option === selectedState ? "bg-[#f7eef4]" : ""
                          }`}
                        >
                          {getStateLabel(option)}
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-3 text-sm text-gray-500">No results found</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

        <div className="mt-8 space-y-8">
          <div className="space-y-3">
            <label className="text- font-medium text-gray-800">
              Description <span className="font-bold text-red-600">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us about yourself"
              className="min-h-[140px] w-full rounded-lg border border-[#d8dbe0] bg-white px-4 py-3 text-base font-medium text-gray-800 placeholder:text-gray-400 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
            />
          </div>



          <div className="space-y-3">
            <div className="rounded-xl border border-dashed border-[#cbd0d6] p-6 text-center">
              {photoPreview ? (
                <div className="relative mx-auto h-36 w-36 overflow-hidden rounded-full border border-gray-200 shadow-sm">
                  <img
                    src={photoPreview}
                    alt="Profile preview"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoPreview("");
                      setPhotoFile(null);
                      setPhotoSelected(false);
                      setHasPersistedPhoto(false);
                    }}
                    className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-600 shadow"
                    aria-label="Remove photo"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-lg font-semibold text-gray-800">
                    Add Profile Photo <span className="font-bold text-red-600">*</span>
                  </p>
                  <p className="mt-1 text-md font-medium text-gray-400">
                    Upload a clear headshot of yourself. JPG, JPEG or PNG, up
                    to {MAX_IMAGE_SIZE_MB}MB.
                  </p>
                </>
              )}

              <div className="mt-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_ACCEPT}
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={handleUploadClick}
                  disabled={isPreparingPhoto}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#d4b4c5] px-6 py-2 text-sm font-semibold text-white transition hover:bg-[#c299b2] focus:outline-none focus:ring-2 focus:ring-[#7a0051]/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <UploadCloud className="h-4 w-4" />
                  {isPreparingPhoto ? "Processing…" : "Upload Photo"}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text- font-medium text-gray-800">
                Services <span className="font-bold text-red-600">*</span>
              </label>
              <span className="text-sm font-medium text-gray-500">
                {selectedServices.length} Selected
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {serviceOptions.map((service) => {
                const active = selectedServices.includes(service);
                return (
                  <button
                    key={service}
                    type="button"
                    onClick={() => toggleService(service)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      active
                        ? "border-[#61013B] bg-[#61013B] text-white"
                        : "border-[#CEB0C2] bg-white text-gray-800"
                    }`}
                  >
                    {service}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <button
              type="button"
              onClick={handleContinue}
              className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold text-white shadow-sm transition ${
                isFormComplete
                  ? "bg-[#61013B] hover:bg-[#4f0130]"
                  : "bg-[#caa8b8] hover:bg-[#b792a3]"
              }`}
              disabled={!isFormComplete || isSubmitting || isPreparingPhoto}
            >
              {isSubmitting ? "Saving..." : "Continue"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full rounded-lg border border-[#7a0051] px-4 py-3 text-center text-base font-semibold text-[#7a0051] transition hover:bg-[#f7eef4]"
            >
              Back
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
